import React, { Children } from 'react';
import { motion } from 'framer-motion';
import { Package, CheckCircle, ArrowRight, ChevronRight, Home, Clock, Shield, Anchor, Plane, DollarSign, Weight, FileText } from 'lucide-react';
import { Button } from '../components/ui/Button';
interface CargoFreightPageProps {
  onNavigate?: (page: string) => void;
}
const fadeIn = {
  hidden: {
    opacity: 0,
    y: 20
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6
    }
  }
};
const staggerContainer = {
  hidden: {
    opacity: 0
  },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.12
    }
  }
};
export function CargoFreightPage({
  onNavigate
}: CargoFreightPageProps) {
  return <div className="flex flex-col bg-white" data-id="element-1430">
      {/* Hero Section */}
      <section className="relative min-h-[520px] lg:min-h-[600px] text-white overflow-hidden -mt-20 sm:-mt-[104px] pt-20 sm:pt-[104px]" data-id="element-1431">
        <div className="absolute inset-0" data-id="element-1432">
          <img src="https://images.unsplash.com/photo-1553413077-190dd305871c?ixlib=rb-4.0.3&auto=format&fit=crop&w=1920&q=80" alt="Cargo freight containers" className="absolute inset-0 w-full h-full object-cover" data-id="element-1433" />
          <div className="absolute inset-0 bg-gray-900/65" data-id="element-1434" />
          <div className="absolute inset-0 bg-gradient-to-r from-gray-900/80 via-gray-900/50 to-gray-900/30" data-id="element-1435" />
          <div className="absolute inset-0 bg-gradient-to-t from-gray-900/60 via-transparent to-gray-900/30" data-id="element-1436" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-24 pb-20 sm:pt-20 sm:pb-20 lg:pt-28 lg:pb-28" data-id="element-1437">
          <nav className="flex items-center gap-2 text-sm text-white/70 mb-8" data-id="element-1438">
            <button onClick={() => onNavigate?.('home')} className="flex items-center gap-1 hover:text-white transition-colors" data-id="element-1439">
              <Home className="w-4 h-4" data-id="element-1440" />
              <span data-id="element-1441">Home</span>
            </button>
            <ChevronRight className="w-4 h-4" data-id="element-1442" />
            <span className="text-white/50" data-id="element-1443">Services</span>
            <ChevronRight className="w-4 h-4" data-id="element-1444" />
            <span className="text-white font-medium" data-id="element-1445">Cargo Freight</span>
          </nav>

          <motion.div initial="hidden" animate="visible" variants={fadeIn} className="max-w-3xl" data-id="element-1446">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-black/40 border border-white/30 text-white text-sm font-medium mb-6 backdrop-blur-sm" data-id="element-1447">
              <Package className="w-4 h-4" data-id="element-1448" />
              Heavy & Bulk Shipments
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight mb-6 leading-tight" style={{
            textShadow: '0 2px 4px rgba(0,0,0,0.5), 0 4px 12px rgba(0,0,0,0.4)'
          }} data-id="element-1449">
              Cargo Freight{' '}
              <span className="text-brand-300" data-id="element-1450">for Large Shipments</span>
            </h1>
            <p className="text-lg sm:text-xl text-white/90 mb-8 max-w-2xl leading-relaxed" style={{
            textShadow: '0 1px 3px rgba(0,0,0,0.5)'
          }} data-id="element-1451">
              Moving heavy goods, machinery, or bulk inventory from the USA to
              Nigeria? Our cargo freight service handles it all — by air or sea,
              with full customs support.
            </p>
            <div className="flex flex-col sm:flex-row gap-4" data-id="element-1452">
              <Button size="lg" className="bg-brand-500 hover:bg-brand-600 text-white border-none shadow-lg px-8" onClick={() => onNavigate?.('contact')} data-id="element-1453">
                Request a Freight Quote <ArrowRight className="ml-2 w-5 h-5" data-id="element-1454" />
              </Button>
              <Button variant="outline" size="lg" className="border-white text-white hover:bg-white/10 px-8" onClick={() => onNavigate?.('contact')} data-id="element-1455">
                Talk to an Expert
              </Button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Air vs Sea */}
      <section className="py-20 lg:py-24 bg-white" data-id="element-1456">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1457">
          <div className="text-center mb-14" data-id="element-1458">
            <span className="inline-block px-4 py-1.5 bg-brand/10 text-brand text-sm font-semibold rounded-full mb-4" data-id="element-1459">
              Freight Options
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4" data-id="element-1460">
              Air Freight vs Sea Freight
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto" data-id="element-1461">
              Choose the right mode based on your timeline and budget.
            </p>
          </div>

          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-100px'
        }} variants={staggerContainer} className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto" data-id="element-1462">
            <motion.div variants={fadeIn} className="bg-gray-50 rounded-2xl p-8 border border-gray-200" data-id="element-1463">
              <div className="w-14 h-14 bg-brand-50 rounded-xl flex items-center justify-center mb-5" data-id="element-1464">
                <Plane className="w-7 h-7 text-brand-600" data-id="element-1465" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-3" data-id="element-1466">
                Air Freight
              </h3>
              <p className="text-gray-600 mb-6 leading-relaxed" data-id="element-1467">
                Fastest option for time-sensitive cargo. Ideal for high-value
                goods and urgent shipments.
              </p>
              <div className="space-y-3 mb-6" data-id="element-1468">
                <div className="flex items-center gap-2" data-id="element-1469">
                  <CheckCircle className="w-4 h-4 text-brand-500 flex-shrink-0" data-id="element-1470" />
                  <span className="text-sm text-gray-700" data-id="element-1471">
                    7–14 day delivery
                  </span>
                </div>
                <div className="flex items-center gap-2" data-id="element-1472">
                  <CheckCircle className="w-4 h-4 text-brand-500 flex-shrink-0" data-id="element-1473" />
                  <span className="text-sm text-gray-700" data-id="element-1474">
                    Best for up to 500 kg
                  </span>
                </div>
                <div className="flex items-center gap-2" data-id="element-1475">
                  <CheckCircle className="w-4 h-4 text-brand-500 flex-shrink-0" data-id="element-1476" />
                  <span className="text-sm text-gray-700" data-id="element-1477">
                    Electronics, apparel, perishables
                  </span>
                </div>
                <div className="flex items-center gap-2" data-id="element-1478">
                  <CheckCircle className="w-4 h-4 text-brand-500 flex-shrink-0" data-id="element-1479" />
                  <span className="text-sm text-gray-700" data-id="element-1480">
                    Full customs clearance
                  </span>
                </div>
              </div>
              <div className="text-sm text-gray-500 bg-white rounded-xl p-3 border border-gray-200" data-id="element-1481">
                <span className="font-semibold text-gray-700" data-id="element-1482">
                  Starting from
                </span>{' '}
                $5/lb for bulk air freight
              </div>
            </motion.div>

            <motion.div variants={fadeIn} className="bg-secondary-900 rounded-2xl p-8 border border-secondary-800 text-white" data-id="element-1483">
              <div className="w-14 h-14 bg-white/10 rounded-xl flex items-center justify-center mb-5" data-id="element-1484">
                <Anchor className="w-7 h-7 text-brand-300" data-id="element-1485" />
              </div>
              <h3 className="text-2xl font-bold text-white mb-3" data-id="element-1486">
                Sea Freight
              </h3>
              <p className="text-gray-400 mb-6 leading-relaxed" data-id="element-1487">
                Most cost-effective for large, heavy shipments. Perfect for
                businesses moving bulk inventory.
              </p>
              <div className="space-y-3 mb-6" data-id="element-1488">
                <div className="flex items-center gap-2" data-id="element-1489">
                  <CheckCircle className="w-4 h-4 text-brand-400 flex-shrink-0" data-id="element-1490" />
                  <span className="text-sm text-gray-300" data-id="element-1491">
                    4–6 week delivery
                  </span>
                </div>
                <div className="flex items-center gap-2" data-id="element-1492">
                  <CheckCircle className="w-4 h-4 text-brand-400 flex-shrink-0" data-id="element-1493" />
                  <span className="text-sm text-gray-300" data-id="element-1494">
                    No weight limit — full containers
                  </span>
                </div>
                <div className="flex items-center gap-2" data-id="element-1495">
                  <CheckCircle className="w-4 h-4 text-brand-400 flex-shrink-0" data-id="element-1496" />
                  <span className="text-sm text-gray-300" data-id="element-1497">
                    Machinery, furniture, vehicles
                  </span>
                </div>
                <div className="flex items-center gap-2" data-id="element-1498">
                  <CheckCircle className="w-4 h-4 text-brand-400 flex-shrink-0" data-id="element-1499" />
                  <span className="text-sm text-gray-300" data-id="element-1500">
                    FCL & LCL options available
                  </span>
                </div>
              </div>
              <div className="text-sm text-gray-400 bg-white/10 rounded-xl p-3 border border-white/10" data-id="element-1501">
                <span className="font-semibold text-white" data-id="element-1502">Best rates</span> for
                shipments over 500 kg
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* What We Handle */}
      <section className="py-20 lg:py-24 bg-gray-50" data-id="element-1503">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1504">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center" data-id="element-1505">
            <motion.div initial={{
            opacity: 0,
            x: -30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6
          }} data-id="element-1506">
              <span className="inline-block px-4 py-1.5 bg-brand/10 text-brand text-sm font-semibold rounded-full mb-6" data-id="element-1507">
                What We Handle
              </span>
              <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6" data-id="element-1508">
                Built for Business Shipments
              </h2>
              <p className="text-gray-600 mb-8 leading-relaxed" data-id="element-1509">
                From small business restocks to full container loads, our
                freight team manages the entire process — pickup, documentation,
                customs, and final delivery.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-1510">
                <div className="flex items-start gap-3 p-4 bg-white rounded-xl border border-gray-200" data-id="element-1511">
                  <Weight className="w-5 h-5 text-brand-500 flex-shrink-0 mt-0.5" data-id="element-1512" />
                  <div data-id="element-1513">
                    <div className="font-semibold text-gray-900 text-sm" data-id="element-1514">
                      Heavy Machinery
                    </div>
                    <div className="text-xs text-gray-500" data-id="element-1515">
                      Industrial equipment & tools
                    </div>
                  </div>
                </div>
                <div className="flex items-start gap-3 p-4 bg-white rounded-xl border border-gray-200" data-id="element-1516">
                  <Package className="w-5 h-5 text-brand-500 flex-shrink-0 mt-0.5" data-id="element-1517" />
                  <div data-id="element-1518">
                    <div className="font-semibold text-gray-900 text-sm" data-id="element-1519">
                      Bulk Inventory
                    </div>
                    <div className="text-xs text-gray-500" data-id="element-1520">
                      Retail & wholesale goods
                    </div>
                  </div>
                </div>
                <div className="flex items-start gap-3 p-4 bg-white rounded-xl border border-gray-200" data-id="element-1521">
                  <Shield className="w-5 h-5 text-brand-500 flex-shrink-0 mt-0.5" data-id="element-1522" />
                  <div data-id="element-1523">
                    <div className="font-semibold text-gray-900 text-sm" data-id="element-1524">
                      Fragile Cargo
                    </div>
                    <div className="text-xs text-gray-500" data-id="element-1525">
                      Specialized packing & handling
                    </div>
                  </div>
                </div>
                <div className="flex items-start gap-3 p-4 bg-white rounded-xl border border-gray-200" data-id="element-1526">
                  <FileText className="w-5 h-5 text-brand-500 flex-shrink-0 mt-0.5" data-id="element-1527" />
                  <div data-id="element-1528">
                    <div className="font-semibold text-gray-900 text-sm" data-id="element-1529">
                      Full Documentation
                    </div>
                    <div className="text-xs text-gray-500" data-id="element-1530">
                      Bill of lading, customs forms
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            x: 30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6,
            delay: 0.2
          }} className="relative" data-id="element-1531">
              <div className="rounded-2xl overflow-hidden shadow-2xl" data-id="element-1532">
                <img src="https://images.unsplash.com/photo-1578575437130-527eed3abbec?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80" alt="Cargo containers at port" className="w-full h-auto" data-id="element-1533" />
              </div>
              <div className="absolute -bottom-6 -left-6 bg-white rounded-xl shadow-lg p-4 border border-gray-100" data-id="element-1534">
                <div className="flex items-center gap-3" data-id="element-1535">
                  <div className="w-12 h-12 bg-brand-50 rounded-lg flex items-center justify-center" data-id="element-1536">
                    <DollarSign className="w-6 h-6 text-brand-600" data-id="element-1537" />
                  </div>
                  <div data-id="element-1538">
                    <div className="text-lg font-bold text-gray-900" data-id="element-1539">
                      Best Rates
                    </div>
                    <div className="text-sm text-gray-500" data-id="element-1540">
                      For bulk shipments
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 lg:py-24 bg-white" data-id="element-1541">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-1542">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="bg-gradient-to-br from-brand-600 to-brand-700 rounded-3xl p-10 sm:p-16 shadow-xl" data-id="element-1543">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4" data-id="element-1544">
              Ready to move your cargo?
            </h2>
            <p className="text-xl text-brand-100 mb-8 max-w-xl mx-auto" data-id="element-1545">
              Contact our freight team for a custom quote tailored to your
              shipment size and timeline.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center" data-id="element-1546">
              <Button variant="white" size="lg" className="px-10" onClick={() => onNavigate?.('contact')} data-id="element-1547">
                Request a Quote
              </Button>
              <Button variant="outline" size="lg" className="border-white text-white hover:bg-white/10 px-10" onClick={() => onNavigate?.('shipping')} data-id="element-1548">
                Start a Shipment
              </Button>
            </div>
          </motion.div>
        </div>
      </section>
    </div>;
}